import { useState, useEffect } from 'react';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { showToast } from '../components/ui/Toast';
import { useSiteContent } from '../context/SiteContentContext';
import {
  TEMPLATE_TYPES, TEMPLATE_LABELS, TEMPLATE_VARIABLES, VARIABLE_HELP, TEMPLATE_FIELDS,
  mergeEmailTemplates, unknownVariables,
} from '../lib/emailTemplates';
import EmailSettings from './EmailSettings';

// Templates are saved online in site_content.email_templates, so the
// email functions send exactly what Andre wrote here.
export default function AdminEmails() {
  const { content, save } = useSiteContent();
  const [templates, setTemplates] = useState(() => mergeEmailTemplates(content.email_templates));
  const [active, setActive] = useState(TEMPLATE_TYPES[0]);
  const [saved, setSaved] = useState(true);
  const [saving, setSaving] = useState(false);

  // The saved copy can arrive after this screen opens; show it unless
  // Andre has already started editing.
  const savedTemplates = content.email_templates;
  useEffect(() => {
    if (saved) setTemplates(mergeEmailTemplates(savedTemplates));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedTemplates]);

  const t = templates[active];
  const variables = TEMPLATE_VARIABLES[active] || [];
  const unknown = unknownVariables(t, active);

  function update(field, value) {
    setTemplates(prev => ({ ...prev, [active]: { ...prev[active], [field]: value } }));
    setSaved(false);
  }

  // Saves every template (edits on other tabs are kept, not lost).
  async function saveAll() {
    if (saving) return;
    setSaving(true);
    const r = await save({ email_templates: templates });
    setSaving(false);
    if (r && r.error) { showToast(r.error, 'error'); return; }
    setSaved(true);
    showToast('Email templates saved');
  }

  return (
    <div>
      <h1 className="adm-title">Email templates</h1>
      <p className="adm-sub">Edit the emails customers receive. Words in {'{curly brackets}'} are filled in for each customer.</p>

      <EmailSettings usesPromo={TEMPLATE_FIELDS.some(f => (templates.abandoned_cart_2?.[f] || '').includes('{promo_code}'))} />

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {TEMPLATE_TYPES.map(type => (
          <button key={type} className={`adm-btn ${active === type ? '' : 'adm-btn--outline'}`} onClick={() => setActive(type)} style={{ fontSize: 11, padding: '5px 10px' }}>
            {TEMPLATE_LABELS[type] || type}
          </button>
        ))}
      </div>

      {t && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 260px', gap: 16, alignItems: 'start' }}>
          <div className="adm-card">
            <div className="adm-field"><label className="adm-label" htmlFor="em-subject">Subject line</label><input id="em-subject" className="adm-input" value={t.subject} onChange={e => update('subject', e.target.value)} /></div>
            <div className="adm-field"><label className="adm-label" htmlFor="em-greeting">Greeting</label><input id="em-greeting" className="adm-input" value={t.greeting} onChange={e => update('greeting', e.target.value)} /></div>
            <div className="adm-field"><label className="adm-label" htmlFor="em-body">Body</label><textarea id="em-body" className="adm-textarea" value={t.body} onChange={e => update('body', e.target.value)} /></div>
            <div className="adm-field"><label className="adm-label" htmlFor="em-button">Button text</label><input id="em-button" className="adm-input" value={t.button_text} onChange={e => update('button_text', e.target.value)} /></div>
            <div className="adm-field"><label className="adm-label" htmlFor="em-footer">Footer note</label><textarea id="em-footer" className="adm-textarea" value={t.footer_note} onChange={e => update('footer_note', e.target.value)} style={{ minHeight: 50 }} /></div>
            {unknown.length > 0 && (
              <p role="alert" style={{ fontSize: 12, color: '#9A3412', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 6, padding: '8px 10px', marginTop: 8, lineHeight: 1.5 }}>
                This email can&rsquo;t fill in {unknown.map(v => '{' + v + '}').join(', ')}. Check the spelling, or use one of the words listed on the right.
              </p>
            )}
          </div>

          <div className="adm-card" aria-label="Variables you can use">
            <div className="adm-card-title">You can use</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {variables.map(v => (
                <li key={v} style={{ padding: '6px 0', borderBottom: '1px solid rgba(74,46,26,0.06)' }}>
                  <code style={{ fontSize: 12, color: '#1E3A2F', fontWeight: 600 }}>{'{' + v + '}'}</code>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2, lineHeight: 1.4 }}>{VARIABLE_HELP[v]}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="adm-save-bar">
        <span className={`adm-save-status ${saved ? 'adm-save-status--saved' : ''}`}>
          {saved ? '✓ Saved' : '● Unsaved'}
        </span>
        <button className="adm-btn" onClick={saveAll} disabled={saving}><IconDeviceFloppy size={14} /> Save templates</button>
      </div>
    </div>
  );
}
